<script setup lang="ts">
import { IconName } from "@/constants";
import type { Directive } from "vue";
import QuestionOutlineIcon from "../../assets/icons/question-outline.svg?component";
import UploadDrawerIcon from "../../assets/icons/upload-drawer.svg?component";
import DownloadDrawerIcon from "../../assets/icons/download-drawer.svg?component";
import CautionOutlineIcon from "../../assets/icons/caution-outline.svg?component";
import OpenFolderIcon from "../../assets/icons/open-folder.svg?component";
import SearchIcon from "../../assets/icons/search.svg?component";
import SortAsc from "../../assets/icons/sort-asc.svg?component";
import SortDesc from "../../assets/icons/sort-desc.svg?component";
import SortDefault from "../../assets/icons/sort-default.svg?component";
import HouseEntranceIcon from "../../assets/icons/house-entrance.svg?component";
import ArrowDownIcon from "../../assets/icons/arrow-down.svg?component";
import ArrowButtonLeftIcon from "../../assets/icons/arrow-button-left.svg?component";
import MultipleNeutralIcon from "../../assets/icons/multiple-neutral.svg?component";
import TaskChecklistIcon from "../../assets/icons/task-checklist.svg?component";
import LogoutIcon from "../../assets/icons/logout.svg?component";
import DatabaseIcon from "../../assets/icons/database.svg?component";
import CogIcon from "../../assets/icons/cog.svg?component";
import PlusOutline from "../../assets/icons/plus-outline.svg?component";
import NavigationMenuVerticalIcon from "../../assets/icons/navigation-menu-vertical.svg?component";
import CloseIcon from "../../assets/icons/close.svg?component";
import CheckOutlineIcon from "../../assets/icons/check-outline.svg?component";
import ArrowUpAndDownIcon from "../../assets/icons/arrow-up-and-down.svg?component";
import UserOutlineIcon from "../../assets/icons/user-outline.svg?component";
import UserOptOutIcon from "../../assets/icons/user-opt-out.svg?component";
import OfficeFileGraphIcon from "../../assets/icons/office-file-graph.svg?component";
import PencilIcon from "../../assets/icons/pencil.svg?component";
import InformationOutlineIcon from "../../assets/icons/information-outline.svg?component";
import LeftArrowOutlineIcon from "../../assets/icons/arrow-left-outline.svg?component";
import RightArrowOutlineIcon from "../../assets/icons/arrow-right-outline.svg?component";
import UpArrowOutlineIcon from "../../assets/icons/arrow-up-outline.svg?component";
import DownArrowOutlineIcon from "../../assets/icons/arrow-down-outline.svg?component";
import CheckIcon from "../../assets/icons/check.svg?component";
import TrashIcon from "../../assets/icons/trash.svg?component";
import CircleFilledIcon from "../../assets/icons/circle-fill.svg?component";
import PerformanceIncreaseIcon from "../../assets/icons/performance-increase.svg?component";
import DataFileGraphIcon from "../../assets/icons/data-file-graph.svg?component";
import DollarCircleIcon from "../../assets/icons/dollar-circle.svg?component";
import MultipleUsersIcon from "../../assets/icons/multiple-users.svg?component";
import ChevronUpIcon from "../../assets/icons/chevron-up.svg?component";
import ChevronDownIcon from "../../assets/icons/chevron-down.svg?component";
import AnalyticsGraphIcon from "../../assets/icons/analytics-graph.svg?component";
import TableToolsIcon from "../../assets/icons/table-tools.svg?component";
import CalendarIcon from "../../assets/icons/calendar.svg?component";
import RefreshArrowIcon from "../../assets/icons/refresh-arrow.svg?component";
import LockIcon from "../../assets/icons/lock.svg?component";
import GlobeIcon from "../../assets/icons/globe.svg?component";
import TrophyIcon from "../../assets/icons/trophy.svg?component";
import BalloonIcon from "../../assets/icons/balloon.svg?component";
import UploadingIcon from "../../assets/icons/uploading.svg?component";
import MailIcon from "../../assets/icons/mail.svg?component";
import PhoneIcon from "../../assets/icons/phone.svg?component";
import ClockIcon from "../../assets/icons/clock.svg?component";
import PercentIcon from "../../assets/icons/percent.svg?component";
import FilterIcon from "../../assets/icons/filter.svg?component";
import ScaleIcon from "../../assets/icons/scale.svg?component";
import BarChartIcon from "../../assets/icons/bar-chart.svg?component";
import ResetIcon from "../../assets/icons/reset.svg?component";
import HierarchyIcon from "../../assets/icons/hierarchy.svg?component";
import ExternalLinkIcon from "../../assets/icons/external-link.svg?component";
import AlertTriangleIcon from "../../assets/icons/alert-triangle.svg?component";
import UserGroupIcon from "../../assets/icons/user-group.svg?component";
import UserIcon from "../../assets/icons/user.svg?component";
import WorkspaceIcon from "../../assets/icons/workspace.svg?component";
import BasicUserIcon from "../../assets/icons/basic-user.svg?component";
import AdminUserIcon from "../../assets/icons/admin-user.svg?component";
import OwnerUserIcon from "../../assets/icons/owner-user.svg?component";
import AccessDeniedIcon from "../../assets/icons/access-denied.svg?component";
import ZoominIcon from "../../assets/icons/zoom-in.svg?component";
import ZoomoutIcon from "../../assets/icons/zoom-out.svg?component";
import MinusOutline from "../../assets/icons/minus-outline.svg?component";
import CoinsIcon from "../../assets/icons/coins.svg?component";
import ListPlusIcon from "../../assets/icons/list-plus.svg?component";
import CurlyBracketsIcon from "../../assets/icons/curly-brackets.svg?component";
import EyeIcon from "../../assets/icons/eye.svg?component";
import HourglassIcon from "../../assets/icons/hourglass.svg?component";
import SparkleIcon from "../../assets/icons/sparkle.svg?component";
import CloudXIcon from "../../assets/icons/cloud-x.svg?component";
import EmptyIcon from "../../assets/icons/empty.svg?component";
import LightningIcon from "../../assets/icons/lightning.svg?component";
import CloneIcon from "../../assets/icons/clone.svg?component";
import UnlockIcon from "../../assets/icons/unlock.svg?component";
import ReviewIcon from "../../assets/icons/review.svg?component";

export interface IconProps {
  name: IconName;
}

defineProps<IconProps>();

// Slightly lazy way to dynamically apply default properties to all svg icons
const vTaiIcon: Directive<SVGElement> = {
  beforeMount: (el) => {
    // keeps SVG size in sync with surrounding typography
    el.setAttribute("width", "1em");
    el.setAttribute("height", "1em");
  }
};
</script>

<template>
  <!-- Font Awesome Icons -->
  <faIcon
    data-testid="tai-fa-icon"
    v-if="$props.name === IconName.Google"
    icon="fa-brands fa-google"
  ></faIcon>
  <faIcon
    v-if="$props.name === IconName.Microsoft"
    icon="fa-brands fa-microsoft"
  ></faIcon>
  <faIcon
    v-if="$props.name === IconName.Hubspot"
    icon="fa-brands fa-hubspot"
  ></faIcon>
  <faIcon
    v-if="$props.name === IconName.Salesforce"
    icon="fa-brands fa-salesforce"
  ></faIcon>

  <!-- Custom SVG Icons (downloaded from https://www.streamlinehq.com/icons/phosphor-regular?search=exclaim&icon=ico_2ZBS2cjIZN9QhHh5) -->
  <QuestionOutlineIcon
    data-testid="tai-custom-icon"
    v-if="$props.name === IconName.QuestionOutline"
    v-tai-icon
  />
  <UploadDrawerIcon v-if="$props.name === IconName.UploadDrawer" v-tai-icon />
  <DownloadDrawerIcon
    v-if="$props.name === IconName.DownloadDrawer"
    v-tai-icon
  />
  <CautionOutlineIcon
    v-if="$props.name === IconName.CautionOutline"
    v-tai-icon
  />
  <OpenFolderIcon v-if="$props.name === IconName.OpenFolder" v-tai-icon />
  <SearchIcon v-if="$props.name === IconName.Search" v-tai-icon />
  <SortAsc v-if="$props.name === IconName.SortAsc" v-tai-icon />
  <SortDesc v-if="$props.name === IconName.SortDesc" v-tai-icon />
  <SortDefault v-if="$props.name === IconName.SortDefault" v-tai-icon />
  <HouseEntranceIcon v-if="$props.name === IconName.HouseEntrance" v-tai-icon />
  <ArrowDownIcon v-if="$props.name === IconName.ArrowDown" v-tai-icon />
  <MultipleNeutralIcon
    v-if="$props.name === IconName.MultipleNeutral"
    v-tai-icon
  />
  <TaskChecklistIcon v-if="$props.name === IconName.TaskChecklist" v-tai-icon />
  <LogoutIcon v-if="$props.name === IconName.Logout" v-tai-icon />
  <ArrowButtonLeftIcon
    v-if="$props.name === IconName.ArrowButtonLeft"
    v-tai-icon
  />
  <DatabaseIcon v-if="$props.name === IconName.Database" v-tai-icon />
  <CogIcon v-if="$props.name === IconName.Cog" v-tai-icon />
  <NavigationMenuVerticalIcon
    v-if="$props.name === IconName.NavigationMenuVertical"
    v-tai-icon
  />
  <CloseIcon v-if="$props.name === IconName.Close" v-tai-icon />
  <PlusOutline v-if="$props.name === IconName.PlusOutline" v-tai-icon />
  <CheckOutlineIcon v-if="$props.name === IconName.CheckOutline" v-tai-icon />
  <ArrowUpAndDownIcon
    v-if="$props.name === IconName.ArrowUpAndDown"
    v-tai-icon
  />
  <UserOutlineIcon v-if="$props.name === IconName.UserOutline" v-tai-icon />
  <UserOptOutIcon v-if="$props.name === IconName.UserOptOut" v-tai-icon />
  <OfficeFileGraphIcon
    v-if="$props.name === IconName.OfficeFileGraph"
    v-tai-icon
  />
  <PencilIcon v-if="$props.name === IconName.Pencil" v-tai-icon />
  <InformationOutlineIcon
    v-if="$props.name === IconName.InformationOutline"
    v-tai-icon
  />
  <LeftArrowOutlineIcon
    v-if="$props.name === IconName.LeftArrowOutline"
    v-tai-icon
  />
  <RightArrowOutlineIcon
    v-if="$props.name === IconName.RightArrowOutline"
    v-tai-icon
  />
  <UpArrowOutlineIcon
    v-if="$props.name === IconName.UpArrowOutline"
    v-tai-icon
  />
  <DownArrowOutlineIcon
    v-if="$props.name === IconName.DownArrowOutline"
    v-tai-icon
  />
  <CheckIcon v-if="$props.name === IconName.Check" v-tai-icon />
  <TrashIcon v-if="$props.name === IconName.Trash" v-tai-icon />
  <CircleFilledIcon v-if="$props.name === IconName.CircleFilled" v-tai-icon />
  <PerformanceIncreaseIcon
    v-if="$props.name === IconName.PerformanceIncrease"
    v-tai-icon
  />
  <DataFileGraphIcon v-if="$props.name === IconName.DataFileGraph" v-tai-icon />
  <DollarCircleIcon v-if="$props.name === IconName.DollarCircle" v-tai-icon />
  <MultipleUsersIcon v-if="$props.name === IconName.MultipleUsers" v-tai-icon />
  <ChevronUpIcon v-if="$props.name === IconName.ChevronUp" v-tai-icon />
  <ChevronDownIcon v-if="$props.name === IconName.ChevronDown" v-tai-icon />
  <AnalyticsGraphIcon
    v-if="$props.name === IconName.AnalyticsGraph"
    v-tai-icon
  />
  <TableToolsIcon v-if="$props.name === IconName.TableTools" v-tai-icon />
  <CalendarIcon v-if="$props.name === IconName.Calendar" v-tai-icon />
  <RefreshArrowIcon v-if="$props.name === IconName.RefreshArrow" v-tai-icon />
  <LockIcon v-if="$props.name === IconName.Lock" v-tai-icon />
  <GlobeIcon v-if="$props.name === IconName.Globe" v-tai-icon />
  <TrophyIcon v-if="$props.name === IconName.Trophy" v-tai-icon />
  <BalloonIcon v-if="$props.name === IconName.Balloon" v-tai-icon />
  <UploadingIcon v-if="$props.name === IconName.Uploading" v-tai-icon />
  <MailIcon v-if="$props.name === IconName.Mail" v-tai-icon />
  <PhoneIcon v-if="$props.name === IconName.Phone" v-tai-icon />
  <ClockIcon v-if="$props.name === IconName.Clock" v-tai-icon />
  <PercentIcon v-if="$props.name === IconName.Percent" v-tai-icon />
  <FilterIcon v-if="$props.name === IconName.Filter" v-tai-icon />
  <ScaleIcon v-if="$props.name === IconName.Scale" v-tai-icon />
  <BarChartIcon v-if="$props.name === IconName.BarChart" v-tai-icon />
  <ResetIcon v-if="$props.name === IconName.Reset" v-tai-icon />
  <HierarchyIcon v-if="$props.name === IconName.Hierarchy" v-tai-icon />
  <ExternalLinkIcon v-if="$props.name === IconName.ExternalLink" v-tai-icon />
  <AlertTriangleIcon v-if="$props.name === IconName.AlertTriangle" v-tai-icon />
  <UserGroupIcon v-if="$props.name === IconName.UserGroup" v-tai-icon />
  <UserIcon v-if="$props.name === IconName.User" v-tai-icon />
  <WorkspaceIcon v-if="$props.name === IconName.Workspace" v-tai-icon />
  <BasicUserIcon v-if="$props.name === IconName.BasicUser" v-tai-icon />
  <AdminUserIcon v-if="$props.name === IconName.AdminUser" v-tai-icon />
  <OwnerUserIcon v-if="$props.name === IconName.OwnerUser" v-tai-icon />
  <AccessDeniedIcon v-if="$props.name === IconName.AccessDenied" v-tai-icon />
  <ZoominIcon v-if="$props.name === IconName.ZoomIn" v-tai-icon />
  <ZoomoutIcon v-if="$props.name === IconName.ZoomOut" v-tai-icon />
  <MinusOutline v-if="$props.name === IconName.MinusOutline" v-tai-icon />
  <CoinsIcon v-if="$props.name === IconName.Coins" v-tai-icon />
  <ListPlusIcon v-if="$props.name === IconName.ListPlus" v-tai-icon />
  <CurlyBracketsIcon v-if="$props.name === IconName.CurlyBrackets" v-tai-icon />
  <EyeIcon v-if="$props.name === IconName.Eye" v-tai-icon />
  <HourglassIcon v-if="$props.name === IconName.Hourglass" v-tai-icon />
  <SparkleIcon v-if="$props.name === IconName.Sparkle" v-tai-icon />
  <CloudXIcon v-if="$props.name === IconName.CloudX" v-tai-icon />
  <EmptyIcon v-if="$props.name === IconName.Empty" v-tai-icon />
  <LightningIcon v-if="$props.name === IconName.Lightning" v-tai-icon />
  <CloneIcon v-if="$props.name === IconName.Clone" v-tai-icon />
  <UnlockIcon v-if="$props.name === IconName.Unlock" v-tai-icon />
  <ReviewIcon v-if="$props.name === IconName.Review" v-tai-icon />
</template>

<style lang="scss" scoped></style>
